import {
  Button,
  EmailField,
  FetchingButton,
  Field,
  FieldContent,
  FieldLabel,
  FieldLegend,
  FieldSet,
  LanguageSelect,
  NameField,
  PhoneField,
} from '@glade-ai/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { useQueryState } from 'nuqs';
import { type FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { UseFormProps } from 'react-hook-form';

import getReturnToFromRouter from '@/components/Auth/helpers/getReturnToFromRouter';
import { SmsOptIn } from '@/components/Auth/SmsOptIn';
import FieldError from '@/components/FieldError';
import { DEFAULT_COUNTRY } from '@/helpers/supported-creator-countries';
import { useToast, useUser } from '@/hooks';
import useIPGeolocation from '@/hooks/useIPGeolocation';
import useNoodleApi from '@/hooks/useNoodleApi';
import { getNextUrl } from '@/lib/Auth/useAfterAuth';
import { logError } from '@/providers/ErrorTracking';
import { clearLocalStorageState } from '@/providers/LocalStorageState';
import { mixpanelAlias, mixpanelTrack } from '@/providers/Mixpanel';
import TranslationContext from '@/providers/TranslationProvider/TranslationContext';
import * as tsClient from '@/tsClient';
import { CountryField } from '@/ui/exemplars/fields/CountryField';
import { PasswordField } from '@/ui/exemplars/fields/PasswordField';
import { type SignUpSchema, signUpSchema } from '@/ui/schemas/signUp';

import pickText from '../../../pickText';
import TermsAndPolicy from '../TermsAndPolicy';
import { type View } from '../types';

import generateErrorMessage from './generateErrorMessage';

type Props = {
  code?: string | null;
  creatorSlug?: string | null;
  onChangeView: (view: View) => void;
  isEmbedded?: boolean;
  isCreator?: boolean;
  hasFinishedOnboarding?: boolean;
  vendor?: string | null;
  activationEmail?: string | null;
  activationName?: string | null;
  priceId?: string | null;
  showTerms?: boolean;
  hideLegend?: boolean;
  isCreatorView?: boolean;
};

const SignUpForm: FC<Props> = ({
  activationEmail,
  activationName,
  code,
  creatorSlug,
  onChangeView,
  hasFinishedOnboarding,
  isEmbedded,
  isCreator,
  vendor,
  priceId,
  showTerms = true,
  hideLegend,
  isCreatorView = false,
}) => {
  const router = useRouter();
  const [queryEmail] = useQueryState('email');
  const [queryName] = useQueryState('name');
  const [businessCategory] = useQueryState('businessCategory');
  const defaultEmail = queryEmail || activationEmail || '';
  const defaultName = queryName || activationName || '';

  const [optedIntoSms, setOptedIntoSms] = useState(true);
  const addToast = useToast();
  const [_user, _setUser, setUserByToken] = useUser();
  const [geolocation] = useIPGeolocation();
  const [onboardingFlow] = useQueryState('onboardingFlow');

  const { language, setLanguage } = useContext(TranslationContext);

  const returnTo = useMemo(
    () =>
      getReturnToFromRouter({
        returnToQuery: router.query.returnTo,
        routerAsPath: router.asPath,
        routerIsReady: router.isReady,
        routerPathname: router.pathname,
      }),
    [router.asPath, router.isReady, router.pathname, router.query.returnTo],
  );

  const signUpForm = useMemo(
    () =>
      ({
        defaultValues: {
          country: (onboardingFlow === 'attorney' ? 'US' : geolocation?.country?.iso_code) || DEFAULT_COUNTRY.code,
          email: defaultEmail,
          name: defaultName,
          password: '',
          phone: '',
        },
        mode: 'onBlur',
        resolver: zodResolver(signUpSchema),
      }) satisfies UseFormProps<SignUpSchema>,
    [defaultEmail, defaultName, geolocation?.country?.iso_code, onboardingFlow],
  );

  const { control, handleSubmit } = useForm<SignUpSchema>(signUpForm);

  const {
    data: signUpResponse,
    error: signUpError,
    fetchingState: signInState,
    getData: passwordSignUp,
  } = useNoodleApi(tsClient.auth.passwordSignUp, {
    healthMonitor: { name: 'sign-up' },
    toastOnError: true,
  });

  const { getData: getPrice } = useNoodleApi(tsClient.getPrice);
  const { data: creator, getData: getCreatorFn } = useNoodleApi(tsClient.getCreator);

  const enableMesagesText = isCreatorView
    ? `Enable SMS Notifications: Opt-in and ${creator?.name} will send you timely notifications and reminders about your case. ${
        creator?.noodlePhoneNumber ? `Messages will come from ${creator?.noodlePhoneNumber}.` : ''
      } No marketing materials will be sent to you`
    : `Opt-in and ${creator?.name} will send you timely notifications and reminders about your case. ${
        creator?.noodlePhoneNumber ? `Messages will come from ${creator?.noodlePhoneNumber}.` : ''
      } No marketing materials will be sent to you.`;

  const handleTapSignIn = (): void => onChangeView('signIn');

  const onSubmit = useCallback(
    (data: SignUpSchema) => {
      passwordSignUp({
        businessCategory: businessCategory || undefined,
        code,
        contactPhoneNumber: data.phone,
        country: data.country,
        creatorSlug: creatorSlug || undefined,
        hasFinishedOnboarding,
        identifier: data.email || defaultEmail,
        ipAddress: geolocation?.ip || undefined,
        isCreator,
        name: data.name,
        optedIntoSms,
        password: data.password,
        preferredLanguage: language,
        returns: ['primaryUserWorkflowId'],
        vendor,
      });
    },
    [
      businessCategory,
      code,
      creatorSlug,
      defaultEmail,
      geolocation?.ip,
      hasFinishedOnboarding,
      isCreator,
      language,
      optedIntoSms,
      passwordSignUp,
      vendor,
    ],
  );

  useEffect(() => {
    if (!signUpResponse) {
      return;
    }

    const { token, isExistingUser, failedClaimCode, primaryUserWorkflowId } = signUpResponse;
    clearLocalStorageState(); // @todo - shouldn't be necessary because of useEffect in Provider, but need to test before removing.
    const user = setUserByToken(token);

    if (user) {
      const url = getNextUrl({
        creatorSlug: creatorSlug || undefined,
        primaryUserWorkflowId: primaryUserWorkflowId || undefined,
        returnTo: returnTo || undefined,
        user,
      });
      router.push(url);
    }

    if (failedClaimCode) {
      addToast(useToast.ToastTypeVariants.ERROR, 'Failed to claim code');
    }
    if (user) {
      mixpanelTrack(isCreator ? 'Creator Sign Up' : 'Sign Up', {
        method: 'Email and password',
      });
      if (!isExistingUser) {
        mixpanelTrack('Created Account', {
          creationMethod: isCreator ? 'Creator Signup (Signup)' : 'Signup',
        });
        mixpanelAlias(user?.id);
      }
    } else {
      logError(new Error('Invalid token returned'));
    }
    // isCreator is only used in mixpanelTrack ok to ignore in dep list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, signUpResponse, setUserByToken]);

  useEffect(() => {
    if (signUpError) {
      mixpanelTrack('Sign up with email error', { reason: signUpError.message });
    }
  }, [signUpError]);

  useEffect(() => {
    if (priceId) {
      getPrice({ priceId });
    }
  }, [priceId, getPrice]);

  useEffect(() => {
    if (creatorSlug) {
      getCreatorFn({ creatorSlug });
    }
  }, [creatorSlug, getCreatorFn]);

  const errorMessage = generateErrorMessage(signUpError);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div role='none' className='mb-6'>
        {!isEmbedded && !hideLegend && (
          <FieldLegend className='heading-sm'>
            {pickText({ creatorSlug, field: 'sign-up-title', isCreator })}
          </FieldLegend>
        )}
        {!vendor && (
          <p className='text-sm'>
            Already have an account?{' '}
            <Button
              variant='link'
              onClick={handleTapSignIn}
              disabled={signInState.isFetching}
              className='!text-[--color-primary] px-0.5'
              data-testid='auth.switchToSignIn'
            >
              Sign in.
            </Button>
          </p>
        )}
      </div>
      <FieldSet disabled={signInState.isFetching}>
        <Controller
          name='name'
          control={control}
          rules={{ required: true }}
          disabled={defaultName !== ''}
          render={({ field, fieldState }) => (
            <NameField
              field={field}
              fieldState={fieldState}
              label={pickText({ creatorSlug, field: 'sign-up-name-label', isCreator })}
              placeholder={pickText({ creatorSlug, field: 'sign-up-name-placeholder', isCreator })}
            />
          )}
        />
        {isCreator && onboardingFlow !== 'attorney' && (
          <Controller
            name='country'
            control={control}
            render={({ field, fieldState }) => <CountryField field={field} fieldState={fieldState} />}
          />
        )}
        <Controller
          name='email'
          control={control}
          rules={{ required: true }}
          disabled={defaultEmail !== ''}
          render={({ field, fieldState }) => <EmailField field={field} fieldState={fieldState} />}
        />
        <Controller
          name='password'
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => <PasswordField field={field} fieldState={fieldState} variant='signUp' />}
        />
        <Controller
          name='phone'
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => <PhoneField field={field} fieldState={fieldState} />}
        />
        <Field id='language-select'>
          <FieldContent>
            <FieldLabel>Preferred language</FieldLabel>
            <LanguageSelect id='language-select' language={language} onLanguageChange={setLanguage} />
          </FieldContent>
        </Field>
        {creator && creator?.noodlePhoneNumber && (
          <SmsOptIn
            id='optedIntoSms'
            label={enableMesagesText}
            isCreatorView={isCreatorView}
            creatorSlug={creator.slug}
            checked={optedIntoSms}
            onCheckedChange={(nextChecked) => setOptedIntoSms(!!nextChecked)}
            disabled={signInState.isFetching}
          />
        )}
        {errorMessage && <FieldError>Error: {errorMessage}</FieldError>}
        <FetchingButton
          variant='primary'
          size='lg'
          type='submit'
          fetching={signInState.isFetching}
          className='mt-4 w-full'
        >
          Sign Up
        </FetchingButton>
      </FieldSet>
      {showTerms && <TermsAndPolicy isCreatorView={isCreatorView} />}
    </form>
  );
};

export default SignUpForm;
